/**
 * Pricing Calculation Utilities
 * Reverse price calculation: Selling Price = (Cost + Fixed) / (1 - PercentTotal - TargetMargin)
 */

import type {
  PricingCalculation,
  MarketplaceColumn,
  CategoryColumnDefaults
} from '../types/database';
import type {
  ShippingRateBracket,
  FBMFromTRConfig,
  GSTConfig,
  FBAProfitConfig,
  FBMProfitConfig,
  ProfitBreakdown,
  ProductData,
} from '../types/pricing';
import { getExchangeRate } from './currency';
import { logger } from './logger';

/**
 * Calculate selling price using reverse calculation
 * Formula: Price = (Cost + Fixed Costs) / (1 - Percent Costs - Target Margin)
 *
 * @param baseCostUSD - Product base cost in USD
 * @param targetCurrency - Target currency for the marketplace
 * @param columns - Marketplace columns with expense definitions
 * @param categoryDefaults - Category-specific expense rates and target margin
 * @returns PricingCalculation with all calculated values
 */
export async function calculatePrice(
  baseCostUSD: number,
  targetCurrency: string,
  columns: MarketplaceColumn[],
  categoryDefaults: CategoryColumnDefaults
): Promise<PricingCalculation> {
  // Get exchange rate
  const exchangeRate = await getExchangeRate('USD', targetCurrency);
  const baseCostLocal = baseCostUSD * exchangeRate;

  // Separate fixed and percent costs from category defaults
  let totalFixedCosts = 0;
  let totalPercentCosts = 0;

  columns.forEach(column => {
    const value = categoryDefaults.columnDefaults[column.key];
    if (value === undefined || value === null) return;

    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;

    if (column.type === 'fixed' || column.type === 'currency') {
      totalFixedCosts += numValue;
    } else if (column.type === 'percent') {
      totalPercentCosts += numValue / 100; // Convert from percentage to decimal
    }
  });

  const targetMargin = categoryDefaults.targetMargin / 100; // Convert from percentage to decimal

  // Reverse calculation: Price = (Cost + Fixed) / (1 - Percent - Margin)
  const denominator = 1 - totalPercentCosts - targetMargin;

  // Prevent division by zero or negative denominator
  if (denominator <= 0) {
    logger.warn('Invalid pricing: percent costs + margin >= 100%');
    return {
      baseCostUSD,
      baseCostLocal,
      exchangeRate,
      totalFixedCosts,
      totalPercentCosts,
      targetMargin,
      calculatedPrice: 0,
      totalExpenses: 0,
      netProfit: 0,
      actualMargin: 0,
    };
  }

  const calculatedPrice = (baseCostLocal + totalFixedCosts) / denominator;

  // Calculate actual expenses based on calculated price
  const percentExpenses = calculatedPrice * totalPercentCosts;
  const totalExpenses = totalFixedCosts + percentExpenses;

  // Net profit and actual margin
  const netProfit = calculatedPrice - baseCostLocal - totalExpenses;
  const actualMargin = calculatedPrice > 0 ? (netProfit / calculatedPrice) * 100 : 0;

  return {
    baseCostUSD,
    baseCostLocal,
    exchangeRate,
    totalFixedCosts,
    totalPercentCosts,
    targetMargin,
    calculatedPrice,
    totalExpenses,
    netProfit,
    actualMargin,
  };
}

/**
 * Calculate individual expense amounts from a selling price
 */
export function calculateExpenseBreakdown(
  sellingPrice: number,
  columns: MarketplaceColumn[],
  columnValues: Record<string, number | string>
): Record<string, number> {
  const breakdown: Record<string, number> = {};

  columns.forEach(column => {
    const value = columnValues[column.key];
    if (value === undefined || value === null) {
      breakdown[column.key] = 0;
      return;
    }

    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;

    if (column.type === 'percent') {
      // Percent of selling price
      breakdown[column.key] = (sellingPrice * numValue) / 100;
    } else if (column.type === 'fixed' || column.type === 'currency') {
      // Fixed amount
      breakdown[column.key] = numValue;
    } else {
      breakdown[column.key] = 0;
    }
  });

  return breakdown;
}

/**
 * Sum all percent-based expenses from columns
 */
export function sumPercentExpenses(
  columns: MarketplaceColumn[],
  columnValues: Record<string, number | string>
): number {
  return columns.reduce((sum, column) => {
    if (column.type !== 'percent') return sum;

    const value = columnValues[column.key];
    if (value === undefined || value === null) return sum;

    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    return sum + numValue;
  }, 0);
}

/**
 * Sum all fixed/currency expenses from columns
 */
export function sumFixedExpenses(
  columns: MarketplaceColumn[],
  columnValues: Record<string, number | string>
): number {
  return columns.reduce((sum, column) => {
    if (column.type !== 'fixed' && column.type !== 'currency') return sum;

    const value = columnValues[column.key];
    if (value === undefined || value === null) return sum;

    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    return sum + numValue;
  }, 0);
}

/**
 * Calculate profit margin from selling price and costs
 */
export function calculateMargin(
  sellingPrice: number,
  totalCost: number,
  totalExpenses: number
): number {
  if (sellingPrice <= 0) return 0;
  const netProfit = sellingPrice - totalCost - totalExpenses;
  return (netProfit / sellingPrice) * 100;
}

/**
 * Calculate net profit from selling price and costs
 */
export function calculateNetProfit(
  sellingPrice: number,
  totalCost: number,
  totalExpenses: number
): number {
  return sellingPrice - totalCost - totalExpenses;
}

/**
 * Format percent value for display
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Validate pricing calculation inputs
 */
export function validatePricingInputs(
  baseCost: number | null,
  targetMargin: number
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (baseCost === null || baseCost < 0) {
    errors.push('Base cost must be a positive number');
  }

  if (targetMargin < 0) {
    errors.push('Target margin cannot be negative');
  }

  if (targetMargin >= 100) {
    errors.push('Target margin must be less than 100%');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get recommended price with .9 ending
 * Rounds up to the nearest whole number and subtracts 0.1 to get .9 ending
 * Examples: 24.50 → 24.9, 29.20 → 29.9, 24.91 → 25.9
 */
export function getRecommendedPrice(calculatedPrice: number): number {
  if (calculatedPrice <= 0) return 0;

  // Get the ceiling (round up to nearest whole number)
  const ceiling = Math.ceil(calculatedPrice);

  // Subtract 0.1 to get .9 ending
  const recommended = ceiling - 0.1;

  // If calculated price already ends in .9 or higher, we go to the next .9
  // e.g., 24.95 → Math.ceil gives 25 → 25 - 0.1 = 24.9, which is less than 24.95
  // So we need to add 1 to get 25.9
  if (recommended < calculatedPrice) {
    return recommended + 1;
  }

  return recommended;
}

// ============================================
// ADVANCED PRICING (FBA/FBM)
// ============================================

/**
 * Calculate shipping cost from desi-based rate brackets
 * Uses linear interpolation between brackets
 * @param brackets - Array of shipping rate brackets sorted by desi
 * @param desi - Product desi (volumetric weight)
 * @param customShipping - Optional custom shipping override
 * @returns Shipping cost in the bracket's currency
 */
export function calculateShippingCost(
  brackets: ShippingRateBracket[],
  desi: number,
  customShipping?: number
): number {
  // If custom shipping is provided, use it
  if (customShipping !== undefined && customShipping !== null) {
    return customShipping;
  }

  // If no brackets or invalid desi, return 0
  if (!brackets || brackets.length === 0 || desi <= 0) {
    return 0;
  }

  // Sort brackets by desi (ascending)
  const sortedBrackets = [...brackets].sort((a, b) => a.desi - b.desi);

  // If desi is below first bracket, use first bracket rate
  if (desi <= sortedBrackets[0].desi) {
    return sortedBrackets[0].rate;
  }

  // If desi is above last bracket, use last bracket rate
  if (desi >= sortedBrackets[sortedBrackets.length - 1].desi) {
    return sortedBrackets[sortedBrackets.length - 1].rate;
  }

  // Find the two brackets to interpolate between
  for (let i = 0; i < sortedBrackets.length - 1; i++) {
    const lower = sortedBrackets[i];
    const upper = sortedBrackets[i + 1];

    if (desi >= lower.desi && desi <= upper.desi) {
      // Linear interpolation
      const desiRange = upper.desi - lower.desi;
      const rateRange = upper.rate - lower.rate;
      const desiOffset = desi - lower.desi;
      const interpolatedRate = lower.rate + (desiOffset / desiRange) * rateRange;

      return interpolatedRate;
    }
  }

  // Fallback (should not reach here)
  return sortedBrackets[sortedBrackets.length - 1].rate;
}

/**
 * Get category-specific customs duty percentage
 * Falls back to default rate if no category match
 * @param fromTRConfig - FBM from Turkey configuration
 * @param category - Product category (partial match, case-insensitive)
 * @returns Customs duty percentage
 */
export function getCustomsDutyPercent(
  fromTRConfig: FBMFromTRConfig,
  category?: string
): number {
  // If no category or no category duties defined, use default
  if (!category || !fromTRConfig.categoryDuties || fromTRConfig.categoryDuties.length === 0) {
    return fromTRConfig.customsDutyPercent;
  }

  // Normalize category for comparison
  const normalizedCategory = category.toLowerCase().trim();

  // Find matching category duty (case-insensitive, partial match)
  const matchingDuty = fromTRConfig.categoryDuties.find(cd => {
    const normalizedCdCategory = cd.category.toLowerCase().trim();
    return normalizedCategory.includes(normalizedCdCategory) ||
           normalizedCdCategory.includes(normalizedCategory);
  });

  return matchingDuty ? matchingDuty.dutyPercent : fromTRConfig.customsDutyPercent;
}

/**
 * Apply GST/VAT to price
 * Handles both included and excluded GST cases
 * @param price - Base price (before or including GST depending on config)
 * @param gstConfig - GST configuration
 * @returns GST details with amounts
 */
export function applyGST(
  price: number,
  gstConfig: GSTConfig
): { priceWithGST: number; gstAmount: number; priceBeforeGST: number } {
  if (!gstConfig.enabled || price <= 0) {
    return {
      priceWithGST: price,
      gstAmount: 0,
      priceBeforeGST: price,
    };
  }

  const gstRate = gstConfig.rate / 100; // Convert to decimal

  if (gstConfig.includedInPrice) {
    // GST is included in price (e.g., AU)
    // priceWithGST = price, priceBeforeGST = price / (1 + gstRate)
    const priceBeforeGST = price / (1 + gstRate);
    const gstAmount = price - priceBeforeGST;

    return {
      priceWithGST: price,
      gstAmount,
      priceBeforeGST,
    };
  } else {
    // GST is added on top (e.g., AE, SA)
    // priceBeforeGST = price, priceWithGST = price * (1 + gstRate)
    const gstAmount = price * gstRate;
    const priceWithGST = price + gstAmount;

    return {
      priceWithGST,
      gstAmount,
      priceBeforeGST: price,
    };
  }
}

/**
 * Calculate FBA profit breakdown
 * Forward calculation: Given selling price, calculate profit
 * @param sellingPrice - Selling price in marketplace currency
 * @param productData - Product cost and desi data (baseCost already converted to marketplace currency)
 * @param categoryDefaults - Category-based fee percentages
 * @param fbaConfig - FBA configuration (shippingPerDesi already converted to marketplace currency, warehouse %)
 * @param countryCode - Country code for warehouse cost (US only)
 * @returns Detailed profit breakdown
 */
export function calculateFBAPrice(
  sellingPrice: number,
  productData: ProductData,
  categoryDefaults: Record<string, number>,
  fbaConfig: FBAProfitConfig,
  countryCode: string
): ProfitBreakdown {
  const avgPrice = sellingPrice;

  // Amazon Cuts (percentages of selling price)
  const sellingFeePercent = categoryDefaults['sellingFeePercent'] || 0;
  const fbaFeePercent = categoryDefaults['fbaFeePercent'] || 0;
  const fbaCostPercent = categoryDefaults['fbaCostPercent'] || 0;
  const refundLossPercent = categoryDefaults['refundLossPercent'] || 0;
  const advertisingPercent = categoryDefaults['advertisingPercent'] || 0;

  const sellingFee = avgPrice * (sellingFeePercent / 100);
  const fbaFee = avgPrice * (fbaFeePercent / 100);
  const fbaCost = avgPrice * (fbaCostPercent / 100);
  const refundLoss = avgPrice * (refundLossPercent / 100);
  const advertisingCost = avgPrice * (advertisingPercent / 100);
  const totalAmazonCuts = sellingFee + fbaFee + fbaCost + refundLoss + advertisingCost;

  // Product & Shipping Costs
  const productCost = productData.baseCost; // Already converted to marketplace currency
  const shippingCost = productData.size * fbaConfig.shippingPerDesi; // shippingPerDesi already converted

  // Warehouse cost (US only)
  const warehouseCost = countryCode === 'US'
    ? avgPrice * (fbaConfig.warehousePercent / 100)
    : 0;

  const totalCosts = productCost + shippingCost + warehouseCost;

  // Profitability
  const netProfit = avgPrice - totalAmazonCuts - totalCosts;
  const profitMargin = avgPrice > 0 ? (netProfit / avgPrice) * 100 : 0;
  const roi = totalCosts > 0 ? (netProfit / totalCosts) * 100 : 0;

  return {
    avgSalePrice: avgPrice,
    sellingFee,
    fbaFee,
    fbaCost,
    fbmCost: 0,
    refundLoss,
    advertisingCost,
    totalAmazonCuts,
    productCost,
    shippingCost,
    customsDuty: 0,
    ddpFee: 0,
    warehouseCost,
    totalCosts,
    netProfit,
    profitMargin,
    roi,
  };
}

/**
 * Calculate FBM profit breakdown
 * Forward calculation: Given selling price, calculate profit
 * @param sellingPrice - Selling price in marketplace currency
 * @param productData - Product cost and desi data
 * @param categoryDefaults - Category-based fee percentages
 * @param fbmConfig - FBM configuration
 * @param shippingBrackets - Shipping rate brackets for the route
 * @param countryCode - Country code for special handling (US)
 * @returns Detailed profit breakdown
 */
export function calculateFBMPrice(
  sellingPrice: number,
  productData: ProductData,
  categoryDefaults: Record<string, number>,
  fbmConfig: FBMProfitConfig,
  shippingBrackets: ShippingRateBracket[],
  countryCode: string
): ProfitBreakdown {
  const avgPrice = sellingPrice;
  const desi = productData.size;
  const category = productData.category;

  // Amazon Cuts (percentages of selling price)
  const sellingFeePercent = categoryDefaults['sellingFeePercent'] || 0;
  const fbmCostPercent = categoryDefaults['fbmCostPercent'] || 0;
  const refundLossPercent = categoryDefaults['refundLossPercent'] || 0;
  const advertisingPercent = categoryDefaults['advertisingPercent'] || 0;

  const sellingFee = avgPrice * (sellingFeePercent / 100);
  const fbmCost = avgPrice * (fbmCostPercent / 100);
  const refundLoss = avgPrice * (refundLossPercent / 100);
  const advertisingCost = avgPrice * (advertisingPercent / 100);
  const totalAmazonCuts = sellingFee + fbmCost + refundLoss + advertisingCost;

  // Product Cost
  const productCost = productData.baseCost; // Assume already converted

  // Get category-based customs duty percentage
  const customsDutyPercent = getCustomsDutyPercent(fbmConfig.fromTR, category);

  // Shipping & Customs calculation
  let shippingCost = 0;
  let customsDuty = 0;
  let ddpFee = 0;
  let warehouseCost = 0;

  if (countryCode === 'US') {
    // US has special handling: TR, LOCAL, or BOTH
    const mode = fbmConfig.shippingMode;

    if (mode === 'TR') {
      // From Turkey
      shippingCost = calculateShippingCost(shippingBrackets, desi);
      customsDuty = avgPrice * (customsDutyPercent / 100);
      ddpFee = fbmConfig.fromTR.ddpFee;
    } else if (mode === 'LOCAL') {
      // From US warehouse
      shippingCost = calculateShippingCost(shippingBrackets, desi);
      warehouseCost = avgPrice * ((fbmConfig.fromLocal?.warehousePercent || 0) / 100);
    } else if (mode === 'BOTH') {
      // 50/50 weighted average
      const trShipping = calculateShippingCost(shippingBrackets, desi);
      const trCustoms = avgPrice * (customsDutyPercent / 100);
      const trDdp = fbmConfig.fromTR.ddpFee;
      const usWarehouse = avgPrice * ((fbmConfig.fromLocal?.warehousePercent || 0) / 100);

      shippingCost = trShipping; // Shipping cost is the same
      customsDuty = trCustoms / 2; // Half customs (only TR portion)
      ddpFee = trDdp / 2; // Half DDP
      warehouseCost = usWarehouse / 2; // Half warehouse
    }
  } else {
    // Other countries: direct from shipping brackets
    shippingCost = calculateShippingCost(shippingBrackets, desi);
    customsDuty = avgPrice * (customsDutyPercent / 100);
    ddpFee = fbmConfig.fromTR.ddpFee;
  }

  const totalCosts = productCost + shippingCost + customsDuty + ddpFee + warehouseCost;

  // Profitability
  const netProfit = avgPrice - totalAmazonCuts - totalCosts;
  const profitMargin = avgPrice > 0 ? (netProfit / avgPrice) * 100 : 0;
  const roi = totalCosts > 0 ? (netProfit / totalCosts) * 100 : 0;

  return {
    avgSalePrice: avgPrice,
    sellingFee,
    fbaFee: 0,
    fbaCost: 0,
    fbmCost,
    refundLoss,
    advertisingCost,
    totalAmazonCuts,
    productCost,
    shippingCost,
    customsDuty,
    ddpFee,
    warehouseCost,
    totalCosts,
    netProfit,
    profitMargin,
    roi,
  };
}
